import { useCallback, useEffect, useState } from "react";
import { Button, Col, Form } from "../lib";
import { type Config, User, useUserSocialsUpdate } from "../services";
import { SocialInput } from "./social-input";

type Props = {
  config: Config;
  onSuccess?: () => void;
  user: User;
};

export function SocialsForm({ config, onSuccess, user }: Props) {
  // Data
  const [updateUserSocials, isUpdating] = useUserSocialsUpdate();

  // State
  const [socials, setSocials] = useState<Record<number, string | null> | null>(
    null
  );

  // Handlers
  const handleChangeItem = useCallback((id: number, value: string | null) => {
    setSocials((prev) => ({ ...prev, [id]: value }));
  }, []);

  const handleSubmit = useCallback(async () => {
    if (socials) {
      await updateUserSocials({ socials });
    }

    onSuccess?.();
  }, [onSuccess, socials, updateUserSocials]);

  // Effects
  useEffect(() => {
    if (config && user && socials === null) {
      const initialSocials: Record<number, string | null> = {};

      config.socials.forEach((social) => {
        const userSocial =
          user?.socials?.find((s) => s.id === social.id) ?? null;

        initialSocials[social.id] = userSocial?.public_id ?? null;
      });

      setSocials(initialSocials);
    }
  }, [config, socials, user]);

  // Render
  if (!socials) {
    return null;
  }

  return (
    <Form>
      <Col mb="2">
        <SocialInput
          id={0}
          name="Phone number (Main)"
          slug="main-phone"
          onChange={handleChangeItem}
          value={user.phone}
          readOnly
        />
        {config.socials.map((social) => (
          <SocialInput
            key={`social-${social.id}`}
            id={social.id}
            name={social.name}
            slug={social.slug}
            onChange={handleChangeItem}
            value={socials[social.id] ?? null}
          />
        ))}
      </Col>
      <Button
        em
        disabled={!user.independent}
        onPress={handleSubmit}
        loading={isUpdating}
      >
        Save changes
      </Button>
    </Form>
  );
}
